import { useEffect, useRef, useState } from 'react';
import { initials, teamColor } from 'pages/PlayerPerformance/playerPerformanceUtils';
import { requestImageSlot } from 'utilities/imageLoadQueue';

type PlayerAvatarSize = 'sm' | 'md' | 'lg';

interface PlayerAvatarProps {
  name: string;
  /** Profile picture URL (SAS-signed); falls back to coloured initials when missing or if it fails to load. */
  imageUrl?: string | null;
  size?: PlayerAvatarSize;
  /** Seed for the initials circle colour (team name on roster screens); defaults to the player's name. */
  colorKey?: string;
}

/** Rendered pixel size per avatar size (width and height attributes, so the image never shifts the layout). */
const AVATAR_PX: Record<PlayerAvatarSize, number> = { sm: 24, md: 36, lg: 48 };

/**
 * The picture is only requested once the avatar is near the viewport, and only a few at a time, because the
 * originals are 1-3 MB. Until it loads (or when it fails) the coloured initials show.
 */
function useQueuedImageSrc(imageUrl: string | null | undefined) {
  const holder = useRef<HTMLSpanElement>(null);
  const [src, setSrc] = useState<string | null>(null);
  const releaseSlot = useRef<(() => void) | null>(null);

  useEffect(() => {
    setSrc(null);
    if (!imageUrl) return undefined;
    let cancelled = false;

    const start = () => {
      const slot = requestImageSlot(() => { if (!cancelled) setSrc(imageUrl); });
      releaseSlot.current = slot.release;
    };

    const node = holder.current;
    if (!node || typeof IntersectionObserver === 'undefined') {
      start();
      return () => { cancelled = true; releaseSlot.current?.(); releaseSlot.current = null; };
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((e) => e.isIntersecting)) {
        observer.disconnect();
        start();
      }
    }, { rootMargin: '200px' });
    observer.observe(node);

    return () => {
      cancelled = true;
      observer.disconnect();
      releaseSlot.current?.();
      releaseSlot.current = null;
    };
  }, [imageUrl]);

  const finish = () => { releaseSlot.current?.(); releaseSlot.current = null; };
  return { holder, src, finish };
}

/** Round player avatar: profile picture when available, otherwise coloured initials. */
const PlayerAvatar: React.FC<PlayerAvatarProps> = ({ name, imageUrl, size = 'md', colorKey }) => {
  const { holder, src, finish } = useQueuedImageSrc(imageUrl);
  return (
    <span
      ref={holder}
      className={`pp-avatar pp-avatar--${size}`}
      style={{ background: teamColor(colorKey ?? name) }}
      data-test='player-avatar'
    >
      {src ? (
        <img
          className='pp-avatar__img'
          src={src}
          alt=''
          width={AVATAR_PX[size]}
          height={AVATAR_PX[size]}
          decoding='async'
          onLoad={finish}
          onError={(e) => { e.currentTarget.style.display = 'none'; finish(); }}
        />
      ) : null}
      <span className='pp-avatar__initials'>{initials(name)}</span>
    </span>
  );
};

export default PlayerAvatar;
